'use client';
import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { tokenInfoNoInterceptor as getTokenInfo } from 'app/api/no-interceptor/tokenInfo';
import { resetTokenInfo, setTokenInfo } from 'app/features/tokenInfo/tokenInfoSlice';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useRefreshToken } from 'app/api/internal/useRefreshToken';
import type { TokenInfo } from '../types/token';

const TokenInfoContext = createContext<TokenInfo | undefined>(undefined);

export function TokenProvider({ children }: { children: ReactNode }) {
  const tokenInfoContext: TokenInfo | undefined = useContext(TokenInfoContext);
  const dispatch = useAppDispatch();
  const tokenInfo = useAppSelector((state) => state.tokenInfo);
  const refreshToken = useRefreshToken();

  const fetchTokenInfo = useCallback(async () => {
    try {
      const response = await getTokenInfo();
      if (response) {
        dispatch(setTokenInfo(response));
      } else {
        dispatch(resetTokenInfo());
      }
    } catch {
      dispatch(resetTokenInfo());
    }
  }, [dispatch]);

  // Set the token info on mount
  useEffect(() => {
    void fetchTokenInfo();
  }, [fetchTokenInfo]);

  // Refresh the token info when the token is about to expire
  useEffect(() => {
    if (!tokenInfo.expiry) return;
    // Refresh 1 minute before expiry. Uses Math.max to ensure this calculation is never negative (defaults to 1 second)
    const refetchTime = Math.max(tokenInfo.expiry - Date.now() - 60000, 1000);

    const refreshTimeout = setTimeout(() => {
      try {
        void refreshToken();
      } catch {
        // Now if refreshToken fails, the ultimate recovery is the user gets logged out and redirected to /login
        throw new Error('Failed to refresh token');
      }
    }, refetchTime);
    return () => {
      clearTimeout(refreshTimeout);
    };
  }, [tokenInfo.expiry, refreshToken]);

  if (tokenInfoContext !== undefined) {
    throw new Error('There can only be one TokenInfo provider in the components tree');
  }

  return <TokenInfoContext.Provider value={tokenInfo}>{children}</TokenInfoContext.Provider>;
}
